import { writable } from 'svelte/store';
import { getFromStorage, saveToStorage } from '../utils/localStorage';

export type Language = 'de' | 'en' | 'fr' | 'ja' | 'es';

const DEFAULT_LANGUAGE: Language = 'en';

/**
 * Get browser language or fallback to default
 */
function getBrowserLanguage(): Language {
  if (typeof window === 'undefined' || !window.navigator || !window.navigator.language) {
    return DEFAULT_LANGUAGE;
  }

  try {
    const browserLang = window.navigator.language.split('-')[0].toLowerCase();
    const supportedLanguages: Language[] = ['de', 'en', 'fr', 'ja', 'es'];
    
    if (supportedLanguages.includes(browserLang as Language)) {
      return browserLang as Language;
    }
  } catch (error) {
    // Fallback if navigator is not available
  }
  
  return DEFAULT_LANGUAGE;
}

/**
 * Initialize language from localStorage or browser
 */
function initLanguage(): Language {
  const stored = getFromStorage<Language>('language', getBrowserLanguage());
  return stored;
}

/**
 * i18n store with localStorage persistence
 */
function createI18nStore() {
  const { subscribe, set, update } = writable<Language>(initLanguage());

  return {
    subscribe,
    setLanguage: (lang: Language) => {
      set(lang);
      saveToStorage('language', lang);
      // Sync with svelte-i18n is handled by the subscribe in i18n/index.ts
    },
    reset: () => {
      const defaultLang = getBrowserLanguage();
      set(defaultLang);
      saveToStorage('language', defaultLang);
      // Sync with svelte-i18n is handled by the subscribe in i18n/index.ts
    }
  };
}

export const i18nStore = createI18nStore();
